import * as LodashFP from 'lodash/fp'

import { reverse } from './array'
import { IStringAnyMap, IStringTMap } from '../types/map'

export type KeyPath = string | string[]

function isNumeric(value: string) {
  return /^-{0,1}\d+$/.test(value)
}

export type KV = [string, any]

export const mapFromKVs = (kvs: KV[]): IStringAnyMap => {
  const result: IStringAnyMap = {}

  for (const kv of kvs) {
    const [key, value] = kv
    result[key] = value
  }

  return result
}

type ObjectKeyValueCallback<T> = (key: string, value: T) => any
export function forEach<T>(obj: IStringTMap<T>, cb: ObjectKeyValueCallback<T>) {
  const keys = Object.keys(obj)
  keys.forEach((key) => {
    cb(key, obj[key])
  })
}

export const convertPath = (path: string): string => {
  const splitPath = path.split('.')
  const transformed = splitPath.reduce((acc, value, index, arr) => {
    const isLast = index === arr.length - 1
    if (isLast) {
      return [...acc, value]
    }

    if (isNumeric(value)) {
      return acc
    }

    const next = arr[index + 1]
    if (isNumeric(next)) {
      return [...acc, `${value}[${next}]`]
    }

    return [...acc, value]
  }, [] as string[])

  return transformed.join('.')
}

export const pathForClassName = (path: KeyPath) => arrayPath(path).join('<>')

export const arrayPath = (path: KeyPath) => {
  if (LodashFP.isArray(path)) {
    return path
  } else {
    return path.split('.')
  }
}

export const stringPath = (path: KeyPath) => {
  if (LodashFP.isArray(path)) {
    return path.join('.')
  } else {
    return path
  }
}

export const isPathEqual = (path1: KeyPath, path2: KeyPath) => {
  return stringPath(path1) === stringPath(path2)
}

export const joinPaths = (ipath1: KeyPath, ipath2: KeyPath) => {
  const path1 = arrayPath(ipath1)
  const path2 = arrayPath(ipath2)

  const combinedPath = [...path1, ...path2]

  if (LodashFP.isString(ipath1)) {
    return stringPath(combinedPath)
  } else {
    return arrayPath(combinedPath)
  }
}

export const createObjectFromPath = (
  inputPath: KeyPath,
  value: any = null,
  initialObject: IStringAnyMap = {},
) => {
  const obj = { ...initialObject }
  let current = obj
  let path = arrayPath(inputPath)

  while (path.length > 1) {
    const [head, ...tail] = path
    path = tail
    if (current[head] === undefined) {
      current[head] = {}
    }
    current = current[head]
  }
  current[path[0]] = value

  return obj
}

export const createObjectFromPaths = (
  ipaths: KeyPath[],
  initializer: (path: KeyPath) => any = () => null,
) => {
  const initial = {}
  const paths = filterNonEdgePaths(ipaths)

  return paths.reduce((object, path, index, arr) => {
    const value = initializer(path)
    const newObject = createObjectFromPath(path, value, object)

    return LodashFP.merge(newObject, initial)
  }, initial)
}

interface PathCreatorOptions {
  prefix: string
  edgesOnly: boolean
}

const cleanArray: (arr: Array<string | null | undefined>) => Array<string> = (
  arr,
) => {
  const newArray = arr.filter((elem) => {
    return elem != null
  }) as string[]

  return newArray
}

export const createPathsFromObject = (
  object: IStringAnyMap,
  options?: Partial<PathCreatorOptions>,
): string[] => {
  const { prefix = '', edgesOnly = false } = options || {}
  const keys = Object.keys(object)
  const initial: string[] = []

  return keys.reduce((acc, key, index, arr) => {
    const newPrefix = prefix === '' ? '' : `${prefix}.`
    const keyWithPrefix = `${newPrefix}${key}`
    if (LodashFP.isPlainObject(object[key])) {
      return cleanArray([
        ...acc,
        edgesOnly ? null : keyWithPrefix,
        ...createPathsFromObject(object[key], {
          prefix: keyWithPrefix,
          edgesOnly,
        }),
      ])
    } else {
      return [...acc, keyWithPrefix]
    }
  }, initial)
}

export const isParentPath = (p1: KeyPath, p2: KeyPath) => {
  const maybeParent = arrayPath(p1)
  const maybeChild = arrayPath(p2)

  if (maybeParent.length >= maybeChild.length) {
    return false
  } else {
    return LodashFP.isEqual(
      LodashFP.take(maybeParent.length, maybeChild),
      maybeParent,
    )
  }
}

export const isSubPath = (p1: KeyPath, p2: KeyPath) => {
  return isParentPath(p2, p1)
}

export const filterNonEdgePaths = (paths: KeyPath[]) => {
  const arrPaths = paths.map(arrayPath)
  const sortedByLen = LodashFP.sortBy((path) => path.length, arrPaths)
  const reversed = reverse(sortedByLen)
  const final: KeyPath[] = []

  reversed.forEach((path, index) => {
    if (index === 0) {
      final.push(path)
    } else {
      const prev = LodashFP.take(index, final)
      let isParent = false
      prev.forEach((pp) => {
        if (isParentPath(path, pp)) {
          isParent = true
        }
      })

      if (!isParent) {
        final.push(path)
      }
    }
  })

  return reverse(final.map(stringPath))
}

export const flattenObject: (input: IStringAnyMap) => IStringAnyMap = (
  input,
) => {
  const edges = createPathsFromObject(input, { edgesOnly: true })

  return edges.reduce((obj, key, index, keys) => {
    const value = LodashFP.getOr(null, key, input)
    const partial = {
      [key]: value,
    }
    return LodashFP.merge(obj, partial)
  }, {})
}

export const isFlatObject = (input: IStringAnyMap) => {
  const keys = Object.keys(input)

  for (const key of keys) {
    if (LodashFP.isPlainObject(input[key])) {
      return false
    }
  }

  return true
}

export const filterKeys: (
  input: IStringAnyMap,
  fn: (key: string) => boolean,
) => IStringAnyMap = (input, fn) => {
  const keys = Object.keys(input)

  return keys.reduce((obj, key, _index, _keys) => {
    if (fn(key) === false) {
      return obj
    } else {
      return { ...obj, [key]: input[key] }
    }
  }, {})
}

export const unflattenObject = (input: IStringAnyMap) => {
  if (!isFlatObject(input)) {
    return input
  }

  const keys = Object.keys(input)
  return createObjectFromPaths(keys, (key) => input[stringPath(key)])
}

export const combinePaths = (p1: KeyPath, p2: KeyPath) => {
  const path1 = arrayPath(p1)
  const path2 = arrayPath(p2)

  return [...path1, ...path2]
}

interface FlattenSubObjectOptions {
  preservePath: boolean
}

export const flattenSubObject = (
  input: IStringAnyMap,
  path: KeyPath,
  options?: Partial<FlattenSubObjectOptions>,
) => {
  const { preservePath = true } = options || {}

  if (preservePath) {
    return filterKeys(input, (key) => isSubPath(key, path))
  } else {
    const unflattened = unflattenObject(input)
    const sub = LodashFP.getOr(null, path, unflattened)

    if (!LodashFP.isPlainObject(sub)) {
      return {}
    }

    return flattenObject(sub)
  }
}

export const copyWithout = (obj: IStringAnyMap, keys: string[]) => {
  const copy = { ...obj }

  for (const key of keys) {
    delete copy[key]
  }

  return copy
}

export const arrayPathFlobulator = (path: string): string => {
  const regex = /\[([0-9]+)\]/g
  return path.replace(regex, '[x]')
}

export const arrayPathUnFlobulator = (path: string): string => {
  const regex = /\[(x)\]/g
  return path.replace(regex, '[0]')
}

export const subEqual = (obj1: IStringAnyMap, obj2: IStringAnyMap) => {
  const obj1Keys = Object.keys(obj1)
  const obj2WithOnlyObj1Keys = LodashFP.pick(obj1Keys, obj2)

  return LodashFP.isEqual(obj1, obj2WithOnlyObj1Keys)
}
